// LeagueTable.js — the sortable league table used by /all-time-table (every
// column) and the season pages (the classic P W D L GF GA GD Pts, with the
// champions and relegated clubs marked). Rows come from tableFor() in
// league.js. Position and club stay pinned while the numbers scroll on phones.

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box } from "@mui/material";
import { BRAND } from "../../theme";
import { clubInfo, clubPath, seasonText } from "./data";

const POS_W = 44; // px; the position and club columns are pinned on phones

const sortName = (t) => clubInfo(t.team).name.replace(/^AFC /, ""); // "AFC Bournemouth" files under B

// sort: the value to order by; text columns sort A–Z first, numbers high
// first. "pos" sorts by the table position (1 first).
export const LEAGUE_COLUMNS = [
  { key: "pos", label: "Pos", title: "Position", sort: (t) => t.pos, asc: true, className: "pos" },
  { key: "club", label: "Club", sort: sortName, text: true, className: "club" },
  { key: "seasons", label: "Seasons", sort: (t) => t.seasons },
  { key: "matches", label: "P", title: "Played", sort: (t) => t.matches },
  { key: "won", label: "W", title: "Won", sort: (t) => t.won },
  { key: "drawn", label: "D", title: "Drawn", sort: (t) => t.drawn },
  { key: "lost", label: "L", title: "Lost", sort: (t) => t.lost },
  { key: "gf", label: "GF", title: "Goals for", sort: (t) => t.goalsFor },
  { key: "ga", label: "GA", title: "Goals against", sort: (t) => t.goalsAgainst },
  { key: "gd", label: "GD", title: "Goal difference", sort: (t) => t.gd },
  { key: "pts", label: "Pts", title: "Points", sort: (t) => t.pts },
  { key: "ppg", label: "PPG", title: "Points per game", sort: (t) => t.ppg },
  { key: "winPct", label: "Win %", title: "Share of matches won", sort: (t) => t.won / t.matches },
  { key: "first", label: "First season", sort: (t) => t.firstSeason },
  { key: "last", label: "Last season", sort: (t) => t.lastSeason },
];

// rows: tableFor() rows. columns: keys of LEAGUE_COLUMNS to show (all by
// default). zoneFor(row): "champion" | "relegated" | null marks a row with a
// coloured edge (the season tables). minWidth: before it scrolls sideways.
export default function LeagueTable({ rows, sort, onSort, isCurrent, columns, zoneFor, minWidth = 900 }) {
  const shown = columns ? LEAGUE_COLUMNS.filter((c) => columns.includes(c.key)) : LEAGUE_COLUMNS;
  const cell = (t, key) => {
    switch (key) {
      case "pos":
        return t.pos;
      case "club":
        return (
          <Box component={RouterLink} to={clubPath(t.team)} sx={{ display: "inline-flex", alignItems: "center", gap: 1.25 }}>
            {t.image && <Box component="img" src={t.image} alt="" loading="lazy" sx={{ width: 22, height: 22, objectFit: "contain", flexShrink: 0 }} />}
            {/* Short names on phones keep the pinned columns narrow. */}
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{clubInfo(t.team).name}</Box>
            <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>{clubInfo(t.team).short}</Box>
          </Box>
        );
      case "seasons":
        return t.seasons;
      case "matches":
        return t.matches.toLocaleString();
      case "won":
        return t.won.toLocaleString();
      case "drawn":
        return t.drawn.toLocaleString();
      case "lost":
        return t.lost.toLocaleString();
      case "gf":
        return t.goalsFor.toLocaleString();
      case "ga":
        return t.goalsAgainst.toLocaleString();
      case "gd":
        return `${t.gd > 0 ? "+" : ""}${t.gd.toLocaleString()}`;
      case "pts":
        return (
          <>
            {t.pts.toLocaleString()}
            {t.deducted > 0 && (
              <Box component="span" title={`Includes a ${t.deducted}-point deduction`} sx={{ color: BRAND.textSecondary }}>
                *
              </Box>
            )}
          </>
        );
      case "ppg":
        return t.ppg.toFixed(2);
      case "winPct":
        return `${((t.won / t.matches) * 100).toFixed(1)}%`;
      case "first":
        return seasonText(t.firstSeason);
      case "last":
        return isCurrent?.(t) ? (
          <Box component="span" sx={{ color: BRAND.cyan, fontWeight: 700 }}>
            {seasonText(t.lastSeason)}
          </Box>
        ) : (
          seasonText(t.lastSeason)
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ overflowX: "auto", mb: 1 }}>
      <Box
        component="table"
        sx={{
          width: "100%",
          minWidth,
          borderCollapse: "separate",
          borderSpacing: 0,
          fontFeatureSettings: '"tnum"',
          "& th, & td": { textAlign: "right", px: 0.85, borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" },
          "& th": { py: 0.25, borderBottomColor: "rgba(255,255,255,0.12)" },
          "& td": { py: 0.9, color: "#d7dae3", fontSize: 14.5, verticalAlign: "middle" },
          "& td.pts": { color: "#fff", fontWeight: 800 },
          // Position and club stay in view while the numbers scroll on phones.
          "& .pos, & .club": { position: "sticky", zIndex: 1, background: BRAND.bg },
          "& .pos": { left: 0, width: POS_W, minWidth: POS_W, textAlign: "left", pl: 0, color: BRAND.textSecondary },
          "& .club": { left: POS_W, textAlign: "left" },
          "& td.club": { color: "#fff", fontWeight: 700 },
          "& td a": { color: "#fff", textDecoration: "none" },
          "& td a:hover": { color: BRAND.cyan },
          "& th button": {
            font: "inherit",
            background: "none",
            border: 0,
            cursor: "pointer",
            p: 0.75,
            mx: -0.75,
            borderRadius: 1,
            color: BRAND.textSecondary,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            "&:hover": { color: "#fff" },
            "&:focus-visible": { outline: `2px solid ${BRAND.cyan}` },
          },
          "& th[aria-sort] button": { color: "#fff" },
          ...(zoneFor && { "& th.pos, & td.pos": { pl: 1.25 } }),
          "& tr.champion td.pos": { boxShadow: `inset 3px 0 0 ${BRAND.cyan}`, color: "#fff", fontWeight: 800 },
          "& tr.relegated td.pos": { boxShadow: "inset 3px 0 0 #ff6b6b" },
          "& tr.relegated td": { background: "rgba(255,107,107,0.05)" },
        }}
      >
        <thead>
          <tr>
            {shown.map((c) => {
              const active = sort.key === c.key;
              return (
                <th key={c.key} scope="col" className={c.className} aria-sort={active ? (sort.desc ? "descending" : "ascending") : undefined}>
                  <button type="button" onClick={() => onSort(c)} title={`Sort by ${(c.title || c.label).toLowerCase()}`}>
                    {c.label}
                    <Box component="span" aria-hidden sx={{ display: "inline-block", width: "1em", ml: 0.25, color: BRAND.cyan }}>
                      {active ? (sort.desc ? "↓" : "↑") : ""}
                    </Box>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.team} className={zoneFor?.(t) || undefined}>
              {shown.map((c) => (
                <td key={c.key} className={c.key === "pts" ? "pts" : c.className}>
                  {cell(t, c.key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}
